import { useState, useEffect } from "react";
import {
  Route,
  Routes,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import useUserStore from "./utils/storeApp";
import AppRoutes from "./AppRoutes";
import "./assets/sass/AppPortal.min.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import CallAPI from "./services/apiCalls";
import {
  usePortalAuthentication,
  getUserRole,
  getUserSession,
  getDefaultLandingPage,
} from "./services/authorizationService";
import axios from "axios";
import { OidcClient } from "@axa-fr/oidc-client";
import { getDomainFromUrl } from "./utils/domainUtilities";

const IdentityServerApp = () => {
  const {
    setUserRole,
    setUserToken,
    SetSharedObject,
    sharedObject,
    setUserSession,
  } = useUserStore((state) => ({
    setUserRole: state.setUserRole,
    setUserToken: state.setUserToken,
    SetSharedObject: state.SetSharedObject,
    sharedObject: state.sharedObject,
    setUserSession: state.setUserSession,
  }));
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processing, SetProcesing] = useState(true);
  const [errFlag, setErrFlag] = useState(false);
  const { accessToken } = useOidcAccessToken();
  const { login, logout, isAuthenticated } = usePortalAuthentication();
  const [customTheme, setCustomTheme] = useState(null);

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error?.response?.status === 401) {
        logout();
      }
      return error;
    },
  );

  const getUserDetails = async (token) => {
    let call = "/users/GetUsersValues";
    const res = await CallAPI(call, false, "get", token);
    SetSharedObject("ResponseCode", res.code);
    if (res && res.status === "SUCCESS") {
      SetSharedObject("vendorsObj", res.data.data.vendors);
      SetSharedObject("codes", res.data.data.codes);
      SetSharedObject("getOrgKinds", res.data.data.getOrgKinds);
      SetSharedObject("orgClassifications", res.data.data.orgClassifications);
      SetSharedObject("getOrgsObj", res.data.data.organizations);
      SetSharedObject("getUserEmails", res.data.data.emails);
      SetSharedObject("getServDesc", res.data.data.getServDesc);
      SetSharedObject("changePasswordFlag", false);

      return res.data.data;
    }
  };
  const getUserEmails = async (token) => {
    let call = "/admin/GetUsersDirectEmails";
    const res = await CallAPI(call, false, "get", token);
    SetSharedObject("ResponseCode", res.code);
    if (res && res.status === "SUCCESS") {
      SetSharedObject("getUserEmails", res.data.data.emails);
      SetSharedObject("codes", res.data.data.codes);
      return res.data.data;
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      SetProcesing(true);

      try {
        // This is being done to apply the custom themes to the entire react app if it exists.
        // Otherwise the default theme will apply
        const customStyles = await import(
          "./custom_portal/" +
            getDomainFromUrl(window.location.hostname) +
            "/assets/stylesheets/CustomTheme.css"
        );

        setCustomTheme(customStyles);
      } catch {}

      if (window !== "undefined") {
        if (location.pathname === "/reset-password") {
          let ob = {
            UserId: searchParams.get("userId"),
            Code: searchParams.get("code"),
            Type: "ResetPassword",
          };
          SetSharedObject("ResetPasswordObj", ob);
          SetProcesing(false);
          navigate("/reset-password");
        } else if (!isAuthenticated) {
          await login();
        } else if (accessToken) {
          if (location.pathname === "/logout") {
            await logout();
          }

          const oidcClient = OidcClient.get();
          let userInfoResponse = await oidcClient.userInfoAsync(true);

          setUserSession(userInfoResponse);
          setUserToken(accessToken);

          SetSharedObject("specialties", null);
          SetSharedObject("UserIdEmail", {});
          SetSharedObject("CustomerPermissionsObj", {});
          let userRole = getUserRole(userInfoResponse);

          setUserRole(userRole);
          let jwtObj = getUserSession(userInfoResponse);
          setUserSession(jwtObj);
          if (userRole === "ADMINISTRATOR") {
            await getDomains(accessToken, jwtObj);
          }

          let getUserDetailsResponse = {};
          if (location.pathname === "" || location.pathname === "/") {
            if (userRole !== "USER") {
              await getUserDetails(accessToken);
            } else {
              getUserDetailsResponse = await getUserEmails(accessToken);
            }
            let navPage = getDefaultLandingPage(
              userInfoResponse,
              getUserDetailsResponse?.emails,
            );
            SetProcesing(false);
            if (navPage === "/webmail") {
              if (window !== "undefined") window.open("../webmail", "_self");
            } else {
              navigate(navPage);
            }
          } else {
            if (userRole !== "USER") {
              await getUserDetails(accessToken);
            } else {
              await getUserEmails(accessToken);
            }
            SetProcesing(false);
          }
        }
      }
    };
    initializeApp();
  }, []);
  const getDomains = async (token, tokenObj) => {
    let call = "/customers/GetUserDomains";
    const res = await CallAPI(call, false, "get", token);
    SetSharedObject("ResponseCode", res.code);

    if (res.status === "SUCCESS") {
      let getDomain = res.data.data;
      let dmId = null;
      if (getDomain.length === 1) {
        dmId = parseInt(getDomain[0].value);
      } else {
        for (let dm of getDomain) {
          if (dm.text === tokenObj.domain_name) {
            dmId = dm.value;
            break;
          }
        }
      }
      setUserSession({ ...tokenObj, domain_id: dmId });
    } else if (res.status === "SERVERERROR") {
      setErrFlag(
        "A system error occurred searching for your results. Please contact support for further assistance.",
      );
    }
  };

  return (
    <>
      <div>
        {customTheme ? <customTheme /> : null}
        {processing ? (
          <div
            className="loader-container"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            aria-labelledby="loader-id"
          >
            <div className="spinner" aria-hidden="true"></div>
            <div className="loading-text" id="loader-id">
              Loading...
            </div>
          </div>
        ) : (
          <Routes>
            {AppRoutes.map((route, index) => {
              const { element, ...rest } = route;
              return <Route key={index} {...rest} element={element} />;
            })}
          </Routes>
        )}
      </div>
    </>
  );
};

export default IdentityServerApp;
